import React, { useState, useEffect } from 'react';
import SiteNav from '@/components/SiteNav';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Globe, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const { login, sessionError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState(sessionError || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sessionError) setError(sessionError);
  }, [sessionError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.user.role === 'admin') {
        navigate('/admin');
      } else {
        const domain = searchParams.get('domain');
        const start = searchParams.get('start');
        if (domain || start === 'checkout') {
          const params = new URLSearchParams({ start: 'checkout' });
          if (domain) params.set('domain', domain);
          const price = searchParams.get('price');
          if (price) params.set('price', price);
          navigate(`/portal?${params.toString()}`);
        } else {
          let stored = null;
          try { stored = JSON.parse(sessionStorage.getItem('ep_intent') || 'null'); } catch (_) {}
          if (stored?.domain) {
            const params = new URLSearchParams({ start: 'checkout', domain: stored.domain });
            if (stored.price || stored.website_price) params.set('price', String(stored.price || stored.website_price));
            navigate(`/portal?${params.toString()}`);
          } else {
            navigate('/portal');
          }
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || sessionError || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6 pt-24 pb-10" data-testid="login-page">
      <SiteNav />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6 min-h-[44px]">
            <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>EasyPeazy</span>
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Welcome Back</h1>
          <p className="text-slate-400">Sign in to manage your websites</p>
        </div>

        <form onSubmit={handleSubmit} className="glass-card p-8 space-y-5">
          {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm" data-testid="login-error">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-900/50 border border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg h-12 px-4 text-slate-100 placeholder:text-slate-600 transition-all outline-none"
              placeholder="you@example.com"
              required
              data-testid="login-email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-slate-900/50 border border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg h-12 px-4 pr-12 text-slate-100 placeholder:text-slate-600 transition-all outline-none"
                placeholder="Enter your password"
                required
                data-testid="login-password-input"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-0 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-slate-500 hover:text-slate-300" aria-label={showPw ? "Hide password" : "Show password"}>
                {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-500 hover:bg-indigo-600 text-white h-12 rounded-lg font-medium transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-indigo-500/20"
            data-testid="login-submit-btn"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <p className="text-center text-sm text-slate-500">
            Don't have an account? <Link to="/signup" className="inline-flex items-center min-h-[44px] text-indigo-400 hover:text-indigo-300" data-testid="login-signup-link">Sign up</Link>
          </p>
          <p className="text-center text-sm text-slate-500">
            <Link to="/forgot-password" className="inline-flex items-center min-h-[44px] text-indigo-400 hover:text-indigo-300" data-testid="forgot-password-link">Forgot your password?</Link>
          </p>
        </form>

        <div className="mt-6 text-center">
          
        </div>
      </div>
    </div>
  );
}
